/**
 * Uplifted from `helper/localServer` (apps/web 143 lines, apps/mobile 131) — the SHARED half.
 *
 * Seeded with the FOUR symbols the offline data layer needs, not the file:
 * `isMaster` + `prepareLsPayloadData` (both apps' `utils/OfflineUtil` import exactly these two),
 * `getMasterDomain` (mobile's `localServer` saga), and web's `getLSRequestPayloadData` in
 * `index.desktop.ts`.
 *
 * 🔴 **This is the module that DISPROVED hazard 28's cycle at symbol level — read that hazard's
 * correction before assuming the rest of the file is blocked.** `helper/localServer` imports
 * `setOffline` from `./OfflineHelper`, and `OfflineHelper` reaches back here through
 * `OfflineUtil.put` → `isMaster`. That looked like a cycle spanning the app/core boundary. It is
 * not: `setOffline` is referenced by exactly ONE symbol in each app — `isMasterOffline`
 * (web lines 102/104, mobile 89/91) — and that symbol is NOT in this closure. None of the four
 * symbols uplifted here touches it, so the cycle stays entirely inside the app.
 *
 * What deliberately STAYS in the apps: `isMasterOffline` and `getMasterInfo` (they need
 * `setOffline`, `nativeHooks.exec` and `isElectron`), `isSlave`, `isBranchAvailable`,
 * `getMasterIp`, `isLocalServerEnable`, `callServer` (a no-op stub on web) and
 * `callLocalServerDb` (`return;`). Add them when something in core actually needs them.
 */
import { isEmpty } from "lodash";
import store from "../../redux-store";
import { getLocalStorage } from "../../utils/localStorage";
import { DEVICEIDENTIFIER } from "../../utils/constants";

/**
 * True when THIS device is the LAN master for its branch.
 *
 * Bodies are identical across the apps bar `isEmpty` vs lodash's `_.isEmpty` (the same
 * function) — so shared.
 *
 * ⚠️ Bare folder imports of `getLocalStorage` and `DEVICEIDENTIFIER` are REQUIRED here, not a
 * hazard-16 defect: `getLocalStorage` is genuinely divergent (web `localStorage`, mobile MMKV)
 * and a `shared.ts` must let the bundler pick per platform. Pinning it to `.desktop` would make
 * the React Native app read browser localStorage — the exact bug ledger hazard 16 reproduced on
 * `PrinterSlice/shared.ts`. `DEVICEIDENTIFIER` lives in `utils/constants/shared.ts`, so bare is
 * the only correct form for it either way.
 */
export function isMaster() {
  let catalogStore = store.getState().catalogStore;
  if (!!catalogStore.posSettings) {
    let posSettings = catalogStore.posSettings || {},
      localServer = posSettings.localServer;
    if (
      localServer &&
      !isEmpty(localServer.deviceIdentifier) &&
      localServer.deviceIdentifier === getLocalStorage(DEVICEIDENTIFIER)
    ) {
      return true;
    }
  }
  return false;
}

/** The master device's LAN origin. Byte-identical in both apps. */
export function getMasterDomain() {
  let localServer = store.getState().catalogStore?.posSettings?.localServer;
  return `http://${localServer.address}`;
}

/**
 * The `{ deviceIdentifier, message }` envelope a slave device POSTs to the master.
 *
 * ⚠️ The two apps read the device-identifier key through DIFFERENT named constants — web
 * `DEVICEIDENTIFIER`, mobile `LOCALSTORAGE_KEYS.deviceIdentifier` — and both are the literal
 * `"deviceIdentifier"`, so the resolved behaviour is identical and this is shared rather than
 * split. The probe asserts the RESOLVED KEY on both platforms, not the constant name, so a
 * future edit that makes the two literals diverge fails the gate instead of passing quietly.
 */
export function prepareLsPayloadData(message: any = {}) {
  let data: any = {
    deviceIdentifier: getLocalStorage(DEVICEIDENTIFIER),
    message: encodeURIComponent(JSON.stringify(message)),
  };
  return data;
}
